import { defineComponent as _defineComponent } from 'vue'
import BCXTable from '@/components/BCXTable.vue';
import { computed, PropType } from 'vue';
import { BaseContract, Contract, ContractStatus } from '@/models/Contracts';
import useFormattedDateTime, { TimeFormatting } from '@/mixins/useFormattedDateTime';
import useI18n from '@/mixins/useI18n';
import BCXUserBannerSimple from '@/components/molecules/BCXUserBannerSimple.vue';
import { BcxTableFilter } from '@/models/BcxTable';
import useUser from '@/mixins/useUser';
import { BaseUserWithRole, BaseCompany } from '@/models/User';
import { logVerbose } from '@/utils/logger';


export default /*#__PURE__*/_defineComponent({
  __name: 'MyProjectsContractTable',
  props: {
  contracts: {
    type: Array as PropType<BaseContract[]>,
    default: () => []
  },
  filter: {
    type: Object as PropType<BcxTableFilter>,
    default: null
  }
},
  setup(__props) {



const { t } = useI18n();
const self = useUser('self');

const fields = ['partner', 'project', 'status', 'changed'];

const { getFormattedTimeByAge } = useFormattedDateTime();

const displayValueCallback = (field: string, obj: Contract) => {
  let partnerId = null;

  const contractorId = (useUser((obj.contractor.user ?? obj.contractor) as BaseUserWithRole)).userId.value
        ?? (useUser((obj.contractor.user ?? obj.contractor) as BaseUserWithRole)).rootUserId.value;
  const clientId = (useUser((obj.client.user ?? obj.client) as BaseUserWithRole)).userId.value
        ?? (useUser((obj.client.user ?? obj.client) as BaseUserWithRole)).rootUserId.value;

  if (self.userId.value === contractorId) {
    partnerId = clientId;
  } else if (self.userId.value === clientId) {
    partnerId = contractorId;
  }
  switch (field) {
    case 'partner': return partnerId;
    case 'project': return obj.title ?? obj.reference ?? '';
    case 'status': return t(`contracts.status.${obj.status}`);
    case 'changed': return getFormattedTimeByAge(obj.lastUpdated, TimeFormatting.ChatMessageOverview);
  }
  return '';
};

const getStatusOrder = (status: ContractStatus): number => {
  switch (status) {
    case ContractStatus.Ended: return 0;
    case ContractStatus.Canceled: return 1;
    case ContractStatus.Open: return 2;
    case ContractStatus.Signed: return 3;
  }
  return -1;
};

const isUserClient = (contract: Contract) => {
  const clientId = contract.client?.user?.userId ?? contract.client?.company?.rootUserId ?? '';
  return self.userId.value === clientId;
};

const getPartnerNameToSortWith = (contract: Contract) => {
  let partner: BaseUserWithRole | BaseCompany | undefined;

  if (isUserClient(contract)) {
    partner = contract.contractor.user ?? contract.contractor.company;
  } else {
    partner = contract.client.user ?? contract.client.company;
  }

  let nameToSortWith = '';

  if (partner) {
    if ('lastname' in partner) {
      // User
      nameToSortWith = `${partner.lastname}, ${partner.firstname}`;
    } else if ('name' in partner) {
      // Company
      nameToSortWith = partner.name;
    }
  }

  return nameToSortWith;
};

const sortValueCallback = (field: string, obj: Contract) => {
  switch (field) {
    case 'partner': return getPartnerNameToSortWith(obj);
    case 'project': return obj.reference;
    case 'status': return getStatusOrder(obj.status);
    case 'changed': return obj.lastUpdated;
  }
  return 0;
};

const searchableCallback = (search: string, obj: Contract) => {
  const block = `${obj.contractor.user?.lastname ?? obj.contractor.company?.name} `
    + `${obj.contractor.user?.firstname ?? obj.contractor.company?.name} `
    + ` ${obj.client.user?.lastname ?? obj.client.company?.name} `
    + `${obj.client.user?.firstname ?? obj.client.company?.name}`
    + `${obj.title ?? obj.reference}`;
  return block.toLocaleLowerCase().includes(search.toLocaleLowerCase());
};

const headerTitles = computed(() => [
  t('contracts.table.columns.partner').toString(),
  t('contracts.table.columns.project').toString(),
  t('contracts.table.columns.status').toString(),
  t('contracts.table.columns.changed').toString(),
]);

const areThereUnreadUpdates = (contract: Contract): boolean => {
  const readDateAsNumber = new Date(contract.readDate).getTime();
  const changeDateAsNumber = isUserClient(contract) ? new Date(contract.contractor?.changeDate).getTime() : new Date(contract.client?.changeDate).getTime();
  logVerbose('Are there any changes? -> readDate', new Date(readDateAsNumber).toISOString(), '| changeDate', new Date(changeDateAsNumber).toISOString(), 'result', readDateAsNumber < changeDateAsNumber, 'reference', contract.reference);
  return readDateAsNumber < changeDateAsNumber;
};


return { __sfc: true,t, self, fields, getFormattedTimeByAge, displayValueCallback, getStatusOrder, isUserClient, getPartnerNameToSortWith, sortValueCallback, searchableCallback, headerTitles, areThereUnreadUpdates, BCXTable, BCXUserBannerSimple }
}

})